import React, { useEffect, useMemo, useState } from "react";
import { Download, CheckCircle2, XCircle, Smartphone } from "lucide-react";
import { fetchPwaAdoption } from "@admin/services/adminApi";

/**
 * Admin Dashboard tile that renders the PWA install-adoption funnel for a
 * trailing window (default 30 days). Data comes from `experiment_events`
 * filtered by `key='pwa_install'`.
 */
function Sparkline({ points, color = "#0A2540" }) {
  const w = 220;
  const h = 36;
  const max = Math.max(1, ...points);
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const d = points
    .map((v, i) => {
      const x = i * step;
      const y = h - (v / max) * (h - 4) - 2;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Metric({ label, value, tone = "slate", testid, icon: Icon }) {
  const toneCls = {
    slate: "text-[#0A2540]",
    emerald: "text-emerald-700",
    rose: "text-rose-700",
    gold: "text-[#0A2540]",
  }[tone];
  return (
    <div className="flex-1 min-w-[80px]" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-400">
        {Icon && <Icon size={12} />}
        {label}
      </div>
      <p className={`mt-1 text-2xl font-extrabold ${toneCls}`} style={{ fontFamily: "Poppins" }}>
        {value}
      </p>
    </div>
  );
}

export default function PwaAdoptionTile({ days = 30 }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    fetchPwaAdoption(days)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Could not load adoption stats"); });
    return () => { alive = false; };
  }, [days]);

  const spark = useMemo(() => {
    if (!data?.series) return [];
    return data.series.map((row) => row.install_prompt_shown || 0);
  }, [data]);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="pwa-adoption-tile">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest font-bold text-slate-400 flex items-center gap-1.5">
            <Smartphone size={12} /> PWA Adoption
          </p>
          <p className="mt-1 text-sm text-slate-500">Install funnel — last {days} days</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Accept rate</p>
          <p className="text-lg font-extrabold text-emerald-700" data-testid="pwa-adoption-accept-rate" style={{ fontFamily: "Poppins" }}>
            {data ? `${data.acceptance_rate}%` : "—"}
          </p>
        </div>
      </div>

      {err ? (
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-xs" data-testid="pwa-adoption-err">{err}</div>
      ) : (
        <>
          <div className="mt-4 flex gap-3">
            <Metric label="Shown" value={data ? data.shown.toLocaleString("en-IN") : "—"} icon={Download} testid="pwa-adoption-shown" />
            <Metric label="Accepted" value={data ? data.accepted.toLocaleString("en-IN") : "—"} tone="emerald" icon={CheckCircle2} testid="pwa-adoption-accepted" />
            <Metric label="Dismissed" value={data ? data.dismissed.toLocaleString("en-IN") : "—"} tone="rose" icon={XCircle} testid="pwa-adoption-dismissed" />
          </div>
          {data && data.shown > 0 && (
            <div className="mt-4">
              <Sparkline points={spark} />
              <div className="mt-2 flex items-center justify-between text-[10px] text-slate-400 uppercase tracking-widest">
                <span>Prompts shown / day</span>
                <span data-testid="pwa-adoption-installed">{data.installed} installed</span>
              </div>
            </div>
          )}
          {data && data.shown === 0 && (
            <p className="mt-4 text-xs text-slate-400" data-testid="pwa-adoption-empty">
              No install prompts recorded in this window yet.
            </p>
          )}
        </>
      )}
    </div>
  );
}
